<script setup>
import { ref } from 'vue'
import { Link } from '@inertiajs/vue3'
import { ShipIcon } from 'lucide-vue-next'
import { DataTable } from '@codinglabsau/ui'
import { ship_image } from '@/helpers'
import Search from '../Tables/Filters/Search.vue'
import moment from 'moment'

const props = defineProps({
  ships: {
    type: Object,
    required: true,
  },
  disableSearch: {
    type: Boolean,
    default: false,
  },
  searchUrl: {
    type: String,
    default: '',
  },
  only: {
    type: Array,
    default: () => ['ships'],
  },
})

const dataTableConfig = {
  columns: [
    {
      text: 'Ship',
      value: 'name',
      sortable: true,
      compact: true,
    },
    {
      text: 'Voyages',
      value: 'voyages',
    },
    {
      text: 'Total Passengers',
      value: 'voyages_sum_passenger_count',
    },
  ],
  collection: props.ships,
}

const showMore = ref({})
</script>

<template>
  <div class="space-y-4">
    <Search v-if="!disableSearch" :search-url :only placeholder="Search ships..." />

    <DataTable v-if="ships.data.length > 0" v-bind="dataTableConfig">
      <template #item.name="{ item }">
        <Link :href="route('ships.show', item)" class="group flex w-fit items-center text-primary">
          <img :src="ship_image(item.thumb)" :alt="item.name" class="mr-4 size-24 rounded-sm" />

          <div class="max-w-lg overflow-hidden pr-5">
            <div class="truncate font-bold group-visited:text-amber-900 group-hover:underline">
              {{ item.name }}
            </div>
            <div class="line-clamp-2 whitespace-normal text-gray-900">{{ item.description }}</div>
          </div>
        </Link>
      </template>

      <template #item.voyages="{ item }">
        <div v-if="item.voyages.length > 0">
          <div v-show="showMore[item.id]">
            <div v-for="voyage in item.voyages" :key="voyage.id">
              <Link
                :href="route('ships.voyages.show', [item.slug, voyage.id])"
                class="hover:underline"
              >
                {{ moment(voyage.departure_date).format('Do MMM YYYY') }}
              </Link>
            </div>
            <button class="text-primary-600 hover:underline" @click="delete showMore[item.id]">
              Show less
            </button>
          </div>
          <div v-show="!showMore[item.id]">
            <div v-for="voyage in item.voyages.slice(0, 4)" :key="voyage.id">
              <Link
                :href="route('ships.voyages.show', [item.slug, voyage.id])"
                class="hover:underline"
              >
                {{ moment(voyage.departure_date).format('Do MMM YYYY') }}
              </Link>
            </div>
            <button
              v-if="item.voyages.length > 4"
              class="text-primary-600 hover:underline"
              @click="showMore[item.id] = true"
            >
              Show more
            </button>
          </div>
        </div>
        <div v-else>Voyages not recorded</div>
      </template>

      <template #item.description="{ item }">
        <div class="max-w-xs">
          <span class="truncate">{{ item.description }}</span>
        </div>
      </template>

      <template #item.voyages_sum_passenger_count="{ item }">
        <div v-if="item.voyages.length > 0">
          {{ parseInt(item.voyages_sum_passenger_count).toLocaleString() }}
        </div>
        <div v-else>0</div>
      </template>
    </DataTable>

    <div v-else class="rounded-md bg-gray-50 p-6 text-center">
      <ShipIcon class="mx-auto size-12 text-gray-400" />
      <h3 class="mt-2 text-sm font-semibold text-gray-900">No ships</h3>
      <p class="mt-1 text-sm text-gray-500">No ships found matching your search.</p>
    </div>
  </div>
</template>
